// src/components/ReportForm.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const ReportForm = ({ formData = {}, setFormData = () => {}, onRejectQtyChange }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [wattageOptions, setWattageOptions] = useState([]);
  const [isWattageOpen, setIsWattageOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const selectedProject = localStorage.getItem('selectedProject');
  const initiator = sessionStorage.getItem('username') || 'Guest';
  const [today, setToday] = useState('');

  useEffect(() => {
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const dd = String(date.getDate()).padStart(2, '0');
    setToday(`${yyyy}-${mm}-${dd}`);
  }, []);

  // Fetch suppliers on mount or project change
  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!selectedProject) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(`${baseURL}get_supplier_names`, {
          projectId: selectedProject,
        });

        const supplierOptions = response.data.map(item => ({
          name: item.name,
          moduleWattage: item.moduleWattage,
          total_quantities: item.total_quantities,
        }));

        setSuppliers(supplierOptions);
      } catch (err) {
        setError('Failed to load suppliers.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [baseURL, selectedProject]);

  // Handle supplier selection
  const handleSupplierChange = (selectedName) => {
    const supplier = suppliers.find(s => s.name === selectedName);

    // Reset wattage and ordered qty
    setFormData(prev => ({
      ...prev,
      issuedTo: selectedName,
      moduleWattage: [],
      totalOrderedQty: ''
    }));

    if (!supplier) {
      setWattageOptions([]);
      return;
    }

    const wattages = supplier.moduleWattage.split(',').map(w => w.trim()).filter(Boolean);
    const quantities = supplier.total_quantities.split(',').map(q => q.trim()).filter(Boolean);

    const options = wattages.map((w, i) => ({
      wattage: w,
      quantity: quantities[i] || '0',
      selected: false
    }));

    setWattageOptions(options);
  };

  // Toggle wattage selection
  const toggleWattage = (wattage) => {
    const updated = wattageOptions.map(item =>
      item.wattage === wattage ? { ...item, selected: !item.selected } : item
    );
    setWattageOptions(updated);

    const selectedWattages = updated.filter(item => item.selected).map(item => item.wattage);
    const totalQty = selectedWattages.reduce((sum, w) => {
      const qty = wattageOptions.find(opt => opt.wattage === w)?.quantity || '0';
      return sum + parseInt(qty);
    }, 0);

    setFormData(prev => ({
      ...prev,
      moduleWattage: selectedWattages,
      totalOrderedQty: totalQty > 0 ? totalQty.toString() : ''
    }));
  };

  // Calculate rejection percentage (3 decimal places)
  const rejectionPercentage = useMemo(() => {
    const ordered = parseFloat(formData.totalOrderedQty) || 0;
    const rejected = parseFloat(formData.totalRejectQty) || 0;

    if (ordered === 0) return '0.000';
    return ((rejected / ordered) * 100).toFixed(3);
  }, [formData.totalOrderedQty, formData.totalRejectQty]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'totalRejectQty') {
      const qty = parseInt(value) || 0;
      setFormData(prev => ({ ...prev, totalRejectQty: qty }));
      onRejectQtyChange?.(qty);
      return;
    }

    if (name === 'totalOrderedQty') return; // Read-only

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Display text for selected wattages
  const selectedWattageText =
    formData.moduleWattage?.length > 0
      ? `${formData.moduleWattage.join(', ')} (${formData.totalOrderedQty || 0})`
      : 'Select wattage(s)';

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
      <h2 className="text-2xl font-bold text-gray-800 mb-8">NCR Report Details</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-300 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Issue Date */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Issue Date *</label>
          <input
            type="date"
            name="issueDate"
            value={formData.issueDate || ''}
            onChange={handleChange}
            max={today}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
          />
        </div>

        {/* Initiator */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Initiator</label>
          <input
            type="text"
            value={initiator}
            disabled
            className="w-full px-4 py-3 bg-gray-100 rounded-lg"
          />
        </div>

        {/* Issued To (Supplier) */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Issued To (Supplier) *</label>
          <select
            value={formData.issuedTo || ''}
            onChange={(e) => handleSupplierChange(e.target.value)}
            required
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
          >
            <option value="">{loading ? 'Loading...' : '-- Select Supplier --'}</option>
            {suppliers.map(s => (
              <option key={s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* Module Wattage Dropdown */}
        <div className="relative">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Module Wattage (Select one or more) *
          </label>
          <div
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white cursor-pointer flex justify-between items-center"
            onClick={() => setIsWattageOpen(!isWattageOpen)}
          >
            <span className={formData.moduleWattage?.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
              {selectedWattageText}
            </span>
            <svg
              className={`w-5 h-5 transition-transform ${isWattageOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {isWattageOpen && wattageOptions.length > 0 && (
            <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {wattageOptions.map(item => (
                <label
                  key={item.wattage}
                  className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={() => toggleWattage(item.wattage)}
                    className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-800">
                    {item.wattage}{' '}
                    <span className="text-sm text-gray-500">(Qty: {item.quantity})</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {isWattageOpen && (
            <div className="fixed inset-0 z-0" onClick={() => setIsWattageOpen(false)} />
          )}
        </div>

        {/* Total Ordered Qty */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Total Ordered Qty <span className="text-blue-600">(Auto-filled)</span>
          </label>
          <input
            type="text"
            value={formData.totalOrderedQty || ''}
            readOnly
            placeholder="Select wattage(s)"
            className="w-full px-4 py-3 bg-gray-100 rounded-lg"
          />
        </div>

        {/* Total Reject Quantity */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Total Reject Quantity *{' '}
            <span className="text-xs text-blue-600">(Sets defect rows)</span>
          </label>
          <input
            type="number"
            name="totalRejectQty"
            value={formData.totalRejectQty || ''}
            onChange={handleChange}
            required
            min="0"
            className="w-full px-4 py-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none font-bold text-red-600"
          />
        </div>

        {/* Rejection Percentage - Now updates instantly */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Rejection %</label>
          <input
            type="text"
            value={`${rejectionPercentage}%`}
            readOnly
            className="w-full px-4 py-3 bg-gray-100 rounded-lg font-medium text-gray-800"
          />
        </div>

        {/* Stage of Detection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Stage of Detection *</label>
          <input
            type="text"
            name="stageOfDetection"
            value={formData.stageOfDetection || ''}
            onChange={handleChange}
            required
            placeholder="e.g. Incoming, In-process"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 outline-none"
          />
        </div>
      </div>
    </div>
  );
};

export default ReportForm;